import { cloneDeep, isEqual, orderBy } from "lodash";
import { Firebase } from "./utils/firebase-utility";
import { createRouter, createWebHistory, type LocationQuery } from "vue-router";

const router = createRouter({
  history: createWebHistory(),

  routes: [
    {
      path: "/login",
      name: "ログイン",
      meta: { isLoginedOnly: false, isAnonymousOnly: true, showNavigationMenu: false },
      component: () => import("./views/login/login-view.vue"),
      props: true,
    },
    {
      path: "/token-login",
      name: "トークンログイン",
      meta: { isLoginedOnly: false, isAnonymousOnly: false, showNavigationMenu: false },
      component: () => import("./views/login/token-login.vue"),
      props: true,
    },
    {
      path: "/register",
      name: "アカウント作成",
      meta: { isLoginedOnly: false, isAnonymousOnly: false, showNavigationMenu: false },
      component: () => import("./views/register/register-view.vue"),
      props: true,
    },
    {
      path: "/",
      name: "ダッシュボード",
      component: () => import("./views/home/home-view.vue"),
      props: true,
    },
    {
      path: "/clients/csv-import",
      name: "取引先インポート",
      component: () => import("./views/clients/csv-import-view.vue"),
      props: true,
    },
    {
      path: "/clients",
      name: "取引先一覧",
      component: () => import("./views/clients/clients-view.vue"),
      props: true,
      children: [
        {
          path: ":clientId",
          name: "取引先詳細",
          component: () => import("./views/clients/client-view.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/invoices/:pathType", // list or history
      name: "請求書",
      meta: { passQuerys: ["sort", "filter", "search"] },
      component: () => import("./views/invoices/invoices-view.vue"),
      props: true,
      children: [
        {
          path: ":invoiceId",
          name: "請求書詳細",
          component: () => import("./views/invoices/invoice-view.vue"),
          props: true,
        },
        {
          path: ":invoiceId/edit",
          name: "請求書編集",
          component: () => import("./views/invoices/invoice-edit-view.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/quotations/:pathType", // list or history
      name: "見積書",
      meta: { passQuerys: ["sort", "filter", "search"] },
      component: () => import("./views/quotations/quotations-view.vue"),
      props: true,
      children: [
        {
          path: ":quotationId",
          name: "見積書詳細",
          component: () => import("./views/quotations/quotation-view.vue"),
          props: true,
        },
        {
          path: ":quotationId/edit",
          name: "見積書編集",
          component: () => import("./views/quotations/quotation-edit-view.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/delivery_notes/:pathType", // list or history
      name: "納品書",
      meta: { passQuerys: ["sort", "filter", "search"] },
      component: () => import("./views/delivery-notes/delivery-notes-view.vue"),
      props: true,
      children: [
        {
          path: ":deliveryNoteId",
          name: "納品書詳細",
          component: () => import("./views/delivery-notes/delivery-note-view.vue"),
          props: true,
        },
        {
          path: ":deliveryNoteId/edit",
          name: "納品書編集",
          component: () => import("./views/delivery-notes/delivery-note-edit-view.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/users",
      name: "ユーザー",
      component: () => import("./views/users/users-view.vue"),
      props: true,
    },
    {
      path: "/configs",
      name: "設定",
      component: () => import("./views/config/configs-view.vue"),
      props: false,

      children: [
        {
          path: "organization",
          name: "組織設定",
          component: () => import("./views/config/config-organization-view.vue"),
          props: false,
        },
        {
          path: "me",
          name: "アカウント設定",
          component: () => import("./views/config/config-me-view.vue"),
          props: false,
        },
        {
          path: "users",
          name: "ユーザー設定",
          component: () => import("./views/config/config-users-view.vue"),
          props: false,
        },
        {
          path: "contact",
          name: "自社担当者設定",
          component: () => import("./views/config/config-contact-view.vue"),
          props: false,
        },
        {
          path: "bank",
          name: "口座設定",
          component: () => import("./views/config/config-bank-view.vue"),
          props: false,
        },
        {
          path: "design",
          name: "デザイン設定",
          component: () => import("./views/config/design/config-design-view.vue"),
          props: false,
          children: [
            {
              path: "quotation",
              name: "見積書デザイン設定",
              component: () => import("./views/config/design/quotation-design-view.vue"),
              props: false,
            },
            {
              path: "invoice",
              name: "請求書デザイン設定",
              component: () => import("./views/config/design/invoice-design-view.vue"),
              props: false,
            },
            {
              path: "delivery-note",
              name: "納品書デザイン設定",
              component: () => import("./views/config/design/delivery-note-design-view.vue"),
              props: false,
            },
          ],
        },
        {
          path: "tags",
          name: "仕訳タグ設定",
          component: () => import("./views/config/config-tags-view.vue"),
          props: false,
        },
      ],
    },
    {
      path: "/report/csv-export",
      name: "CSV出力",
      meta: { isLoginedOnly: true, isAnonymousOnly: false, showNavigationMenu: true },
      component: () => import("./views/report/csv-export.vue"),
      props: true,
    },
    {
      path: "/mail-action",
      name: "メールリンク",
      meta: { isLoginedOnly: false, isAnonymousOnly: false, showNavigationMenu: false },
      component: () => import("./views/mail-action/mail-action.vue"),
      props: true,
    },
    {
      path: "/story-book",
      name: "コンポーネントのサンプル",
      component: () => import("./views/story-book/story-book-view.vue"),
      props: true,
      children: [
        {
          path: "button",
          name: "ボタン",
          component: () => import("./views/story-book/button-view.vue"),
          props: true,
        },
      ],
    },
    {
      path: "/:path(.*)",
      redirect: { path: "/", replace: true },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  const isLoginedOnly = to.matched.some((record) => record.meta.isLoginedOnly ?? true);
  const isAnonymousOnly = to.matched.some((record) => record.meta.isAnonymousOnly);

  // ログインページログイン状態を見る必要がなければ即時表示
  if (!isLoginedOnly && !isAnonymousOnly) {
    next();
    return;
  }

  const isLogined = await Firebase.logined;

  // ログイン済なのにログインページ表示しようとしてたらホームか、指定遷移先に遷移
  if (isLogined && isAnonymousOnly) {
    if (to.query.redirect) {
      next({ path: to.query.redirect as string });
    } else {
      next({ path: "/" });
    }
    return;
  }

  // ログイン必須ページなのにログインしていないなら、ログインページへ遷移
  if (isLoginedOnly && !isLogined) {
    next({ path: "/login", query: { redirect: to.fullPath, ...to.query } });
    return;
  }

  // ログイン済みだけど初回登録終わっていないときは、初回登録フローへ
  if (isLoginedOnly && isLogined && Firebase.organization.value?.registerStep !== "100") {
    next({ path: "/register" });
    return;
  }
  next();
});

router.beforeResolve((_to, from, next) => {
  if (_to.redirectedFrom && _to.redirectedFrom.meta.isResolvedQuery) {
    next();
    return;
  }

  const to = { ..._to, query: cloneDeep(_to.query) };
  try {
    const hasQuery = (query: LocationQuery) => {
      return query && Object.keys(query).length > 0;
    };
    const hasPassQuery = (query: LocationQuery, passQuerys: string[]) => {
      return query && Object.keys(query).filter((v) => passQuerys.includes(v)).length > 0;
    };

    // トップレベルのルートは同じ
    const passQuerys = to.matched[0]?.meta.passQuerys;
    if (from.matched[0] && to.matched[0] && from.matched[0].name === to.matched[0].name && Array.isArray(passQuerys)) {
      if (Array.isArray(passQuerys) && !hasPassQuery(to.query, passQuerys) && to.query._ === undefined) {
        if (hasPassQuery(from.query, passQuerys)) {
          for (const key of passQuerys) {
            if (from.query[key]) {
              to.query[key] = from.query[key];
            }
          }
          to.query._ = "";
        }
      }
    }
    // _だけがある場合は消す
    if (hasQuery(to.query)) {
      const keys = Object.keys(to.query);
      if (keys.length == 1 && keys[0] == "_") {
        delete to.query._;
      }
    }
    // _以外がある場合は_を追加
    if (hasQuery(to.query)) {
      const keys = Object.keys(to.query);
      if (keys.length > 0) {
        to.query._ = "";
      }
    }

    // queryの順番をソート
    if (hasQuery(to.query)) {
      const query: LocationQuery = {};
      for (const key of orderBy(Object.keys(to.query))) {
        query[key] = to.query[key];
      }
      to.query = query;
    }
  } finally {
    if (isEqual(_to.query, to.query) && isEqual(Object.keys(_to.query), Object.keys(to.query))) {
      next();
    } else {
      _to.meta.isResolvedQuery = true;
      next(to);
    }
  }
});

export default router;
